import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FileDown, Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DuplicatesPanel } from "@/pages/admin/DuplicatesPanel";
import { RUGBY_STATUS, RugbyRow } from "@/pages/admin/RugbyRow";
import { fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const Stat = ({ l, v, testId }) => (
  <div data-testid={testId} className="bg-[#131927] p-4 outline outline-1 outline-slate-800">
    <div className="eyebrow">{l}</div>
    <div className="mt-1.5 font-data text-xl font-bold text-white">{v ?? "—"}</div>
  </div>
);

const Pick = ({ value, onChange, options, all, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="h-11 w-full border-slate-800 bg-[#131927] text-xs sm:w-48"><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-80 border-slate-800 bg-[#131927]">
      <SelectItem value="all">{all}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const uniq = (teams, key) => [...new Set(teams.flatMap((t) => t[key] || []))].sort();

const saveBlob = async (path, name) => {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement("a"), { href: url, download: name }).click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(formatApiError(e)); }
};

const RunHistory = ({ runs }) => (
  <div>
    <h3 className="mb-3 font-display text-xl font-extrabold uppercase">Run history</h3>
    <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
      {runs.map((r) => (
        <div key={r.id} data-testid={`rugby-run-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
          <span className="font-data text-xs text-slate-400">{fmtDate(r.started_at)} {new Date(r.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          <span className={`rounded-full border px-2.5 py-0.5 font-data text-[11px] uppercase ${r.status === "running" ? "border-blue-500/30 text-blue-400" : r.status === "failed" ? "border-red-500/30 text-red-400" : "border-emerald-500/30 text-emerald-400"}`}>{r.status}</span>
          <span className="flex-1 text-slate-300">{Array.isArray(r.scope) ? `${r.scope.length} team(s)` : "All teams"} · {r.message}</span>
          <span className="font-data text-[11px] text-slate-500">+{r.added || 0} added · {r.updated || 0} updated · {r.protected || 0} protected · {r.off_roster || 0} off roster</span>
        </div>
      ))}
      {!runs.length && <p className="p-6 text-sm text-slate-500">No runs yet.</p>}
    </div>
  </div>
);

export const RugbyTab = () => {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ q: "", org: "all", div: "all", conf: "all", status: "all" });
  const load = useCallback(() => api.get("/admin/rugby/teams").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const running = data?.runs?.some((r) => r.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const scrape = async (slugs, name) => {
    try { await api.post("/admin/rugby/scrape", slugs ? { slugs } : {}); toast.success(`Finding rosters: ${name || "all programs"}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const all = useMemo(() => data?.teams || [], [data]);
  const teams = useMemo(() => all.filter((t) =>
    (f.status === "all" || (t.status || "pending") === f.status) && (f.org === "all" || t.organizations?.includes(f.org)) &&
    (f.div === "all" || t.divisions?.includes(f.div)) && (f.conf === "all" || t.conferences?.includes(f.conf)) &&
    (!f.q || `${t.school} ${t.location} ${t.conference}`.toLowerCase().includes(f.q.toLowerCase()))), [all, f]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  if (!data) return <p className="text-sm text-slate-500">Loading rugby programs</p>;
  const s = data.summary;
  const filtered = teams.length !== all.length;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-xl text-sm text-slate-400">Women's college rugby programs (NIRA, NCR, CRAA). Varsity rosters come from athletics sites; club rosters are looked up on club-sports and team sites. Paste a link or upload a CSV to fill gaps. Protected players are never changed.</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="rugby-template-button" onClick={() => saveBlob("/admin/rugby/csv-template", "knowhername-rugby-roster-template.csv")} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-sm hover:border-white"><FileDown className="h-4 w-4" /> CSV template</button>
          <button data-testid="rugby-download-button" onClick={() => saveBlob("/admin/rugby/export", "knowhername-rugby-rosters.json")} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-sm hover:border-white"><Download className="h-4 w-4" /> Download JSON</button>
          {filtered && <button data-testid="rugby-scrape-filtered-button" disabled={running || !teams.length} onClick={() => scrape(teams.map((t) => t.slug), `${teams.length} filtered programs`)} className="inline-flex items-center gap-2 rounded-full border border-[#FF2D78]/60 px-4 py-2.5 text-sm text-white hover:bg-[#FF2D78]/15 disabled:opacity-50"><RefreshCw className="h-4 w-4" /> Re-scrape filtered ({teams.length})</button>}
          <button data-testid="rugby-scrape-all-button" onClick={() => scrape(null)} disabled={running} className="inline-flex items-center gap-2 rounded-full bg-[#FF2D78] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#E01F66] disabled:opacity-60">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {running ? data.runs[0]?.message || "Running" : "Find & scrape all"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-slate-800 md:grid-cols-6">
        <Stat l="Programs" v={s.teams} testId="rugby-summary-teams" />
        <Stat l="Found" v={`${s.found} of ${s.teams}`} testId="rugby-summary-found" />
        <Stat l="Needs review" v={s.needs_review} testId="rugby-summary-review" />
        <Stat l="Not found" v={s.not_found} testId="rugby-summary-not-found" />
        <Stat l="Players" v={s.players.toLocaleString()} testId="rugby-summary-players" />
        <Stat l="Last run" v={fmtDate(s.last_run_at)} testId="rugby-summary-last-run" />
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="rugby-search-input" value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search programs, cities or conferences"
            className="h-11 w-full rounded-lg border border-slate-800 bg-[#131927] pl-11 pr-4 text-sm outline-none focus:border-[#FF2D78]" />
        </div>
        <Pick value={f.org} onChange={set("org")} options={uniq(all, "organizations")} all="All organizations" testId="rugby-filter-org" />
        <Pick value={f.div} onChange={set("div")} options={uniq(all, "divisions")} all="All divisions" testId="rugby-filter-division" />
        <Pick value={f.conf} onChange={set("conf")} options={uniq(all, "conferences")} all="All conferences" testId="rugby-filter-conference" />
      </div>
      <div className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-800 bg-[#131927] p-1">
        {["all", ...Object.keys(RUGBY_STATUS)].map((v) => (
          <button key={v} data-testid={`rugby-filter-status-${v}`} onClick={() => set("status")(v)}
            className={`rounded-md px-3 py-1.5 font-data text-[11px] uppercase ${f.status === v ? "bg-[#FF2D78] text-white" : "text-slate-400 hover:text-white"}`}>
            {v === "all" ? "All" : RUGBY_STATUS[v][0]} <span className="opacity-60">{v === "all" ? all.length : all.filter((t) => (t.status || "pending") === v).length}</span>
          </button>
        ))}
        <span data-testid="rugby-showing-count" className="ml-auto px-3 font-data text-[11px] text-slate-500">Showing {teams.length} of {all.length}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full min-w-[1180px] text-sm">
          <thead className="bg-[#131927]"><tr className="text-left">{["Program", "Division / conference", "Status", "Players", "Source · last scrape", "Fill gap"].map((h) => <th key={h} className="eyebrow px-4 py-3 font-normal">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-800">
            {teams.map((t) => <RugbyRow key={`${t.slug}-${t.roster_url_override || ""}`} t={t} running={running} onScrape={scrape} onChanged={load} />)}
          </tbody>
        </table>
      </div>
      <DuplicatesPanel sport="rugby" px="rugby" refreshKey={data.runs[0]?.finished_at} />
      <RunHistory runs={data.runs} />
    </div>
  );
};
